import * as React from 'react'
import { Check } from 'lucide-react'
import { useUpdateOwnVendor } from '@/hooks/useVendors'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Field } from '@/components/ui/field'
import { FormActions } from '@/components/ui/form-actions'
import { Input } from '@/components/ui/input'
import { FormError } from '@/components/ui/form-error'
import { updateVendorInputSchema } from '@lib/input/vendor.input'
import type { OwnVendorProfileDto, VendorContactDto } from '@lib/dto/vendor-profile.dto'

type ContactKey = keyof VendorContactDto

interface ContactField {
  key: ContactKey
  label: string
  type: 'url' | 'tel' | 'email'
  placeholder: string
  hint?: string
}

/** In the order they are shown on the profile. */
const FIELDS: readonly ContactField[] = [
  { key: 'website', label: 'Website', type: 'url', placeholder: 'https://example.com' },
  { key: 'phone', label: 'Phone', type: 'tel', placeholder: '(512) 555-0100' },
  {
    key: 'publicEmail',
    label: 'Public email',
    type: 'email',
    placeholder: 'hello@example.com',
    hint: 'Shown on your profile. It can differ from the address you sign in with.',
  },
  { key: 'instagramUrl', label: 'Instagram', type: 'url', placeholder: 'https://instagram.com/yourname' },
  { key: 'facebookUrl', label: 'Facebook', type: 'url', placeholder: 'https://facebook.com/yourname' },
  { key: 'tiktokUrl', label: 'TikTok', type: 'url', placeholder: 'https://tiktok.com/@yourname' },
  { key: 'pinterestUrl', label: 'Pinterest', type: 'url', placeholder: 'https://pinterest.com/yourname' },
]

function initial(contact: VendorContactDto): Record<ContactKey, string> {
  return {
    website: contact.website ?? '',
    phone: contact.phone ?? '',
    publicEmail: contact.publicEmail ?? '',
    instagramUrl: contact.instagramUrl ?? '',
    facebookUrl: contact.facebookUrl ?? '',
    tiktokUrl: contact.tiktokUrl ?? '',
    pinterestUrl: contact.pinterestUrl ?? '',
  }
}

function isContactKey(key: PropertyKey | undefined): key is ContactKey {
  return FIELDS.some((field) => field.key === key)
}

/**
 * How venue staff and planners reach the company, as it chooses to show:
 * business details, never the address anyone signs in with. An empty box
 * clears its detail.
 */
export function ContactCard({ profile }: { profile: OwnVendorProfileDto }) {
  const update = useUpdateOwnVendor()
  const [values, setValues] = React.useState(() => initial(profile.contact))
  const [errors, setErrors] = React.useState<Partial<Record<ContactKey, string>>>({})
  const [failure, setFailure] = React.useState<string | null>(null)
  const [saved, setSaved] = React.useState(false)

  async function save(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setFailure(null)
    setSaved(false)
    const parsed = updateVendorInputSchema.safeParse(values)
    if (!parsed.success) {
      const next: Partial<Record<ContactKey, string>> = {}
      for (const issue of parsed.error.issues) {
        const key = issue.path[0]
        if (isContactKey(key) && next[key] === undefined) next[key] = issue.message
      }
      setErrors(next)
      return
    }
    setErrors({})
    try {
      await update.mutateAsync(parsed.data)
      setSaved(true)
    } catch (e) {
      setFailure(requestErrorMessage(e))
    }
  }

  return (
    <Card id="contact">
      <CardHeader>
        <CardTitle>Contact</CardTitle>
        <CardDescription>How venue staff and planners can reach you. Leave out anything you’d rather not show.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={(e) => void save(e)} noValidate>
          <div className="grid gap-4 sm:grid-cols-2">
            {FIELDS.map((field) => (
              <Field key={field.key} label={field.label} htmlFor={`contact-${field.key}`} hint={field.hint} error={errors[field.key]}>
                <Input
                  id={`contact-${field.key}`}
                  type={field.type}
                  inputMode={field.type}
                  placeholder={field.placeholder}
                  value={values[field.key]}
                  onChange={(e) => {
                    const value = e.target.value
                    setValues((current) => ({ ...current, [field.key]: value }))
                    setSaved(false)
                  }}
                />
              </Field>
            ))}
          </div>
          {failure ? <FormError>{failure}</FormError> : null}
          <FormActions>
            {saved ? (
              <span className="inline-flex items-center gap-1 text-sm text-ok-ink">
                <Check className="h-4 w-4" aria-hidden /> Saved.
              </span>
            ) : null}
            <Button type="submit" disabled={update.isPending}>
              Save
            </Button>
          </FormActions>
        </form>
      </CardContent>
    </Card>
  )
}
